import {
  Chip as MUIChip,
  type ChipProps as MUIChipProps,
  styled,
} from "@mui/material";

export interface ChipProps extends MUIChipProps {
  /**
   * The variant to use for styling.
   * @default light
   */
  variant?: "light" | MUIChipProps["variant"];
}

export const CUSTOM_COLORS: ChipProps["color"][] = [
  "primary",
  "secondary",
  "accent",
  "s800",
  "yellowc",
  "redc",
  "greenc",
  "violetc",
  "orangec",
  "pinkc",
];

interface ColorConfig {
  color: string;
  backgroundColor: string;
}

type MUIColorsKeys = "success" | "error" | "info" | "warning";

const MUILightColors: Record<MUIColorsKeys, ColorConfig> = {
  success: { color: "success", backgroundColor: "#E8F5E9" },
  error: { color: "error", backgroundColor: "#FEEBEE" },
  info: { color: "info", backgroundColor: "#E1F5FE" },
  warning: { color: "warning", backgroundColor: "#FFF3E0" },
};

const MUIFilledBackgroundColors: Record<MUIColorsKeys, string> = {
  success: "#4CAF50",
  error: "#FF9800",
  info: "#EF5350",
  warning: "#03A9F4",
};

const BaseChip = styled(MUIChip)(({ theme, icon, size = "medium" }) => ({
  width: "fit-content",
  padding: size === "small" ? "3px 5px" : "4px 8px",
  "& .MuiChip-label": {
    padding: 0,
    paddingLeft: !icon ? 0 : size === "small" ? 2 : 4,
  },
  "& .MuiChip-icon": {
    width: size === "small" ? 10 : 14,
    height: size === "small" ? 10 : 14,
    margin: 0,
    padding: 0,
  },
  ...(size === "medium" && { height: 25, ...theme.typography.caption }),
  ...(size === "small" && { height: 20, ...theme.typography.overline }),
  textTransform: "none",
}));

const FilledChip = styled(BaseChip, {
  shouldForwardProp: (prop) => prop !== "variant",
})(({ theme, color = "primary" }) => ({
  color: CUSTOM_COLORS.includes(color)
    ? theme.palette.primary.contrastText
    : undefined,
  backgroundColor: CUSTOM_COLORS.includes(color)
    ? theme.palette[color].main
    : MUIFilledBackgroundColors[color as MUIColorsKeys],
  "&.Mui-disabled": {
    opacity: 1,
    color: theme.palette.primary.contrastText,
    backgroundColor: "#C2C5D5",
  },
}));

const LightChip = styled(BaseChip, {
  shouldForwardProp: (prop) => prop !== "variant",
})(({ theme, color = "primary" }) => ({
  width: "fit-content",
  color: CUSTOM_COLORS.includes(color)
    ? theme.palette[color].D30
    : theme.palette[color].main,
  backgroundColor: CUSTOM_COLORS.includes(color)
    ? theme.palette[color].L90
    : MUILightColors[color as MUIColorsKeys].backgroundColor,
  "&.Mui-disabled": {
    opacity: 1,
    color: theme.palette.action.disabled,
    backgroundColor: "#E7E8EE",
  },
}));

export function Chip({ variant = "light", color, ...props }: ChipProps) {
  return variant === "light" ? (
    <LightChip color={color} {...props} />
  ) : (
    <FilledChip color={color} {...props} />
  );
}
